import { FaFileAlt, FaFolder, FaPlus, FaDownload, FaEye, FaEdit, FaTrash, FaSave, FaTimes, FaSearch, FaFilter, FaSort, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileArchive, FaFile, FaTag, FaCalendarDay, FaUser } from "react-icons/fa";
import { useState } from "react";
import { useAuth } from "./AuthContext";

export default function ResourceLibrary() {
  const { user } = useAuth();
  const isAdmin = user?.isProfileAdmin || false;

  const [resources, setResources] = useState([
    {
      id: 1,
      title: "Staff Council Constitution",
      description: "Official constitution document outlining the structure, purpose, and governance of the Staff Council.",
      category: "Governance",
      type: "pdf",
      fileSize: "2.4 MB",
      uploadedBy: "Josie Simm",
      uploadDate: "2025-12-01",
      tags: ["Constitution", "Governance", "Official"],
      url: "#",
      viewCount: 45,
    },
    {
      id: 2,
      title: "Meeting Protocol Guidelines",
      description: "Standard operating procedures for conducting Staff Council meetings, including agenda preparation and minute-taking.",
      category: "Policies",
      type: "pdf",
      fileSize: "1.8 MB",
      uploadedBy: "Mohammed Waseem Chaudry",
      uploadDate: "2025-12-15",
      tags: ["Meetings", "Protocol", "Guidelines"],
      url: "#",
      viewCount: 32,
    },
    {
      id: 3,
      title: "Committee Formation Template",
      description: "Template for creating new committees, including charter structure and role definitions.",
      category: "Templates",
      type: "docx",
      fileSize: "456 KB",
      uploadedBy: "Karol Tolpa",
      uploadDate: "2026-01-02",
      tags: ["Committee", "Template"],
      url: "#",
      viewCount: 18,
    },
    {
      id: 4,
      title: "Budget Request Form 2026",
      description: "Annual budget request form for all committees and departments.",
      category: "Finance",
      type: "xlsx",
      fileSize: "892 KB",
      uploadedBy: "Josie Simm",
      uploadDate: "2026-01-03",
      tags: ["Budget", "Finance", "2026"],
      url: "#",
      viewCount: 28,
    },
    {
      id: 5,
      title: "Staff Survey Results Q4 2025",
      description: "Comprehensive analysis of staff satisfaction survey conducted in December 2025.",
      category: "Reports",
      type: "pdf",
      fileSize: "3.2 MB",
      uploadedBy: "Eudora Okine",
      uploadDate: "2026-01-04",
      tags: ["Survey", "Q4", "2025", "Results"],
      url: "#",
      viewCount: 52,
    },
    {
      id: 6,
      title: "Professional Development Workshop Slides",
      description: "Presentation materials from the January 2026 professional development workshop.",
      category: "Training",
      type: "pptx",
      fileSize: "5.6 MB",
      uploadedBy: "Karol Tolpa",
      uploadDate: "2026-01-05",
      tags: ["Workshop", "Training", "Development"],
      url: "#",
      viewCount: 21,
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest, name, views
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    type: "pdf",
    fileSize: "",
    tags: [],
    url: "",
  });

  const categories = ["Governance", "Policies", "Templates", "Finance", "Reports", "Training", "Other"];
  const fileTypes = ["pdf", "docx", "xlsx", "pptx", "jpg", "png", "zip", "other"];

  const getFileIcon = (type) => {
    const icons = {
      pdf: <FaFilePdf style={{ color: "#f56565" }} />,
      docx: <FaFileWord style={{ color: "#4299e1" }} />,
      doc: <FaFileWord style={{ color: "#4299e1" }} />,
      xlsx: <FaFileExcel style={{ color: "#48bb78" }} />,
      xls: <FaFileExcel style={{ color: "#48bb78" }} />,
      pptx: <FaFilePowerpoint style={{ color: "#ed8936" }} />,
      ppt: <FaFilePowerpoint style={{ color: "#ed8936" }} />,
      jpg: <FaFileImage style={{ color: "#9f7aea" }} />,
      jpeg: <FaFileImage style={{ color: "#9f7aea" }} />,
      png: <FaFileImage style={{ color: "#9f7aea" }} />,
      gif: <FaFileImage style={{ color: "#9f7aea" }} />,
      zip: <FaFileArchive style={{ color: "#718096" }} />,
      rar: <FaFileArchive style={{ color: "#718096" }} />,
    };
    return icons[type.toLowerCase()] || <FaFile style={{ color: "#a0aec0" }} />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Governance: "#667eea",
      Policies: "#f6ad55",
      Templates: "#48bb78",
      Finance: "#ed8936",
      Reports: "#4299e1",
      Training: "#9f7aea",
      Other: "#a0aec0",
    };
    return colors[category] || "#a0aec0";
  };

  const handleAddResource = () => {
    setEditingResource(null);
    setFormData({
      title: "",
      description: "",
      category: "",
      type: "pdf",
      fileSize: "",
      tags: [],
      url: "",
    });
    setShowAddModal(true);
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description,
      category: resource.category,
      type: resource.type,
      fileSize: resource.fileSize,
      tags: resource.tags,
      url: resource.url,
    });
    setShowAddModal(true);
  };

  const handleDeleteResource = (resourceId) => {
    if (window.confirm("Are you sure you want to delete this resource?")) {
      setResources(resources.filter(r => r.id !== resourceId));
    }
  };

  const handleSaveResource = () => {
    if (!formData.title || !formData.category) {
      alert("Please fill in required fields: Title and Category");
      return;
    }

    if (editingResource) {
      setResources(resources.map(r =>
        r.id === editingResource.id
          ? { ...r, ...formData }
          : r
      ));
    } else {
      const newResource = {
        ...formData,
        id: Math.max(...resources.map(r => r.id)) + 1,
        uploadedBy: user?.name || "Unknown",
        uploadDate: new Date().toISOString().split('T')[0],
        viewCount: 0,
      };
      setResources([...resources, newResource]);
    }

    setShowAddModal(false);
    setFormData({
      title: "",
      description: "",
      category: "",
      type: "pdf",
      fileSize: "",
      tags: [],
      url: "",
    });
  };

  const handleAddTag = (tag) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tag.trim()] });
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  // Filter and sort
  let filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === "All" || resource.category === filterCategory;
    const matchesType = filterType === "All" || resource.type === filterType;
    return matchesSearch && matchesCategory && matchesType;
  });

  filteredResources = [...filteredResources].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.uploadDate) - new Date(a.uploadDate);
      case "oldest":
        return new Date(a.uploadDate) - new Date(b.uploadDate);
      case "name":
        return a.title.localeCompare(b.title);
      case "views":
        return b.viewCount - a.viewCount;
      default:
        return 0;
    }
  });

  const stats = {
    total: resources.length,
    byCategory: categories.map(cat => ({
      name: cat,
      count: resources.filter(r => r.category === cat).length,
    })).filter(c => c.count > 0),
    totalViews: resources.reduce((acc, r) => acc + r.viewCount, 0),
  };

  return (
    <div
      style={{
        padding: "40px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#2d3748",
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <FaFolder style={{ color: "#667eea" }} />
            Resource Library
          </h1>
          <p style={{ color: "#718096", fontSize: "16px", margin: 0 }}>
            Access documents, templates, and resources for Staff Council
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleAddResource}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaPlus /> Add Resource
          </button>
        )}
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Total Resources
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#667eea" }}>
            {stats.total}
          </div>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Total Views
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#48bb78" }}>
            {stats.totalViews}
          </div>
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: "14px", color: "#718096", marginBottom: "8px" }}>
            Categories
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#9f7aea" }}>
            {stats.byCategory.length}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          marginBottom: "20px",
        }}
      >
        {/* Search Bar */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ position: "relative" }}>
            <FaSearch
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#a0aec0",
              }}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search resources by title, description, or tags..."
              style={{
                width: "100%",
                padding: "12px 12px 12px 40px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaFilter style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Filters:</span>
          </div>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="All">All File Types</option>
            {fileTypes.map(type => (
              <option key={type} value={type}>{type.toUpperCase()}</option>
            ))}
          </select>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
            <FaSort style={{ color: "#667eea" }} />
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">By Name</option>
              <option value="views">Most Viewed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      {filteredResources.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
            gap: "20px",
          }}
        >
          {filteredResources.map(resource => (
            <div
              key={resource.id}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e2e8f0",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.08)";
              }}
            >
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ fontSize: "32px" }}>
                    {getFileIcon(resource.type)}
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#a0aec0", marginBottom: "4px" }}>
                      {resource.type.toUpperCase()} • {resource.fileSize}
                    </div>
                    <span
                      style={{
                        padding: "4px 10px",
                        background: getCategoryColor(resource.category),
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {resource.category}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={() => handleEditResource(resource)}
                      style={{
                        padding: "6px",
                        background: "#f7fafc",
                        color: "#667eea",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteResource(resource.id)}
                      style={{
                        padding: "6px",
                        background: "#fff5f5",
                        color: "#f56565",
                        border: "1px solid #feb2b2",
                        borderRadius: "6px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>

              {/* Title and Description */}
              <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#2d3748", margin: "0 0 8px 0" }}>
                {resource.title}
              </h3>
              <p style={{ color: "#718096", fontSize: "13px", lineHeight: "1.6", margin: "0 0 12px 0" }}>
                {resource.description}
              </p>

              {/* Tags */}
              {resource.tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
                  {resource.tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: "4px 8px",
                        background: "#f7fafc",
                        color: "#667eea",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FaTag style={{ fontSize: "9px" }} />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Meta Info */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11px", color: "#a0aec0" }}>
                  <div style={{ marginBottom: "2px" }}>
                    <FaUser style={{ fontSize: "10px", marginRight: "4px" }} />
                    {resource.uploadedBy}
                  </div>
                  <div>
                    <FaCalendarDay style={{ fontSize: "10px", marginRight: "4px" }} />
                    {new Date(resource.uploadDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ fontSize: "11px", color: "#a0aec0" }}>
                  <FaEye style={{ fontSize: "10px", marginRight: "4px" }} />
                  {resource.viewCount} views
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => window.open(resource.url, '_blank')}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <FaEye /> View
                </button>
                <button
                  onClick={() => {
                    // Simulate download
                    alert(`Downloading ${resource.title}...`);
                  }}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#f7fafc",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <FaDownload /> Download
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "60px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <FaFolder style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }} />
          <p style={{ color: "#718096", fontSize: "16px" }}>
            No resources found matching your criteria
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#2d3748", marginBottom: "20px" }}>
              {editingResource ? "Edit Resource" : "Add New Resource"}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Title */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Resource title"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the resource..."
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              {/* Category and Type */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    File Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    {fileTypes.map(type => (
                      <option key={type} value={type}>{type.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* File Size and URL */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    File Size
                  </label>
                  <input
                    type="text"
                    value={formData.fileSize}
                    onChange={(e) => setFormData({ ...formData, fileSize: e.target.value })}
                    placeholder="e.g., 2.4 MB"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                    URL
                  </label>
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://..."
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "600", color: "#2d3748" }}>
                  Tags
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: "6px 10px",
                        background: "#667eea",
                        color: "white",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {tag}
                      <FaTimes
                        style={{ cursor: "pointer", fontSize: "10px" }}
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Type a tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
                <button
                  onClick={handleSaveResource}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <FaSave /> Save Resource
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#f7fafc",
                    color: "#4a5568",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

